import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2bfdf896"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Saves Manager", -1))
const _hoisted_2 = { class: "modal-footer" }
const _hoisted_3 = { class: "field" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "field-title" }, "Create Save", -1))
const _hoisted_5 = { class: "field-buttons" }
const _hoisted_6 = { class: "footer" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { style: {"flex-grow":"1"} }, null, -1))

import projInfo from "data/projInfo.json";
import Modal from "components/Modal.vue";
import player, { PlayerData, stringifySave } from "game/player";
import settings from "game/settings";
import { getUniqueID, loadSave, save, newSave } from "util/save";
import { ComponentPublicInstance, computed, nextTick, ref, shallowReactive, watch } from "vue";
import Select from "./fields/Select.vue";
import Text from "./fields/Text.vue";
import Save from "./Save.vue";
import Draggable from "vuedraggable";
import LZString from "lz-string";
import { ProxyState } from "util/proxies";

export type LoadablePlayerData = Omit<Partial<PlayerData>, "id"> & { id: string; error?: unknown };


export default /*#__PURE__*/_defineComponent({
  setup(__props, { expose }) {

const isOpen = ref(false);
const modal = ref<ComponentPublicInstance<typeof Modal> | null>(null);

expose({
    open() {
        isOpen.value = true;
    }
});

const importingFailed = ref(false);
const saveToImport = ref("");
const selectedPreset = ref<string | null>(null);

watch(saveToImport, importedSave => {
    if (importedSave) {
        nextTick(() => {
            try {
                if (importedSave[0] === "{") {
                    // plaintext. No processing needed
                } else if (importedSave[0] === "e") {
                    // Assumed to be base64, which starts with e
                    importedSave = decodeURIComponent(escape(atob(importedSave)));
                } else if (importedSave[0] === "ᯡ") {
                    // Assumed to be lz, which starts with ᯡ
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    importedSave = LZString.decompressFromUTF16(importedSave)!;
                } else {
                    console.warn("Unable to determine preset encoding", importedSave);
                    importingFailed.value = true;
                    return;
                }
                const playerData = JSON.parse(importedSave);
                if (typeof playerData !== "object") {
                    importingFailed.value = true;
                    return;
                }
                const id = getUniqueID();
                playerData.id = id;
                save(playerData);
                saveToImport.value = "";
                importingFailed.value = false;

                settings.saves.push(id);
            } catch (e) {
                importingFailed.value = true;
            }
        });
    } else {
        importingFailed.value = false;
    }
});

let bankContext = require.context("raw-loader!../../saves", true, /\.txt$/);
let bank = ref(
    bankContext.keys().reduce((acc: Array<{ label: string; value: string }>, curr) => {
        // .slice(2, -4) strips the leading ./ and the trailing .txt
        acc.push({
            label: curr.slice(2, -4),
            value: bankContext(curr).default
        });
        return acc;
    }, [])
);

const cachedSaves = shallowReactive<Record<string, LoadablePlayerData | undefined>>({});
function getCachedSave(id: string) {
    if (cachedSaves[id] == null) {
        let save = localStorage.getItem(id);
        if (save == null) {
            cachedSaves[id] = { error: `Save doesn't exist in localStorage`, id };
        } else if (save === "dW5kZWZpbmVk") {
            cachedSaves[id] = { error: `Save is undefined`, id };
        } else {
            try {
                if (save[0] === "{") {
                    // plaintext. No processing needed
                } else if (save[0] === "e") {
                    // Assumed to be base64, which starts with e
                    save = decodeURIComponent(escape(atob(save)));
                } else if (save[0] === "ᯡ") {
                    // Assumed to be lz, which starts with ᯡ
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    save = LZString.decompressFromUTF16(save)!;
                } else {
                    console.warn("Unable to determine preset encoding", save);
                    importingFailed.value = true;
                    cachedSaves[id] = { error: "Unable to determine preset encoding", id };
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    return cachedSaves[id]!;
                }
                cachedSaves[id] = { ...JSON.parse(save), id };
            } catch (error) {
                cachedSaves[id] = { error, id };
                console.warn(
                    `SavesManager: Failed to load info about save with id ${id}:\n${error}\n${save}`
                );
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return cachedSaves[id]!;
}
// Wipe cache whenever the modal is opened
watch(isOpen, isOpen => {
    if (isOpen) {
        Object.keys(cachedSaves).forEach(key => delete cachedSaves[key]);
    }
});

const saves = computed(() =>
    settings.saves.reduce((acc: Record<string, LoadablePlayerData>, curr: string) => {
        acc[curr] = getCachedSave(curr);
        return acc;
    }, {})
);

function exportSave(id: string) {
    let saveToExport;
    if (player.id === id) {
        saveToExport = stringifySave(player[ProxyState]);
    } else {
        saveToExport = JSON.stringify(saves.value[id]);
    }
    switch (projInfo.exportEncoding) {
        default:
            console.warn(`Unknown save encoding: ${projInfo.exportEncoding}. Defaulting to lz`);
        case "lz":
            saveToExport = LZString.compressToUTF16(saveToExport);
            break;
        case "base64":
            saveToExport = btoa(unescape(encodeURIComponent(saveToExport)));
            break;
        case "plain":
            break;
    }

    // Put on clipboard. Using the clipboard API asks for permissions and stuff
    const el = document.createElement("textarea");
    el.value = saveToExport;
    document.body.appendChild(el);
    el.select();
    el.setSelectionRange(0, 99999);
    document.execCommand("copy");
    document.body.removeChild(el);
}

function duplicateSave(id: string) {
    if (player.id === id) {
        save();
    }

    const playerData = { ...saves.value[id], id: getUniqueID() };
    save(playerData as PlayerData);

    settings.saves.push(playerData.id);
}

function deleteSave(id: string) {
    settings.saves = settings.saves.filter((save: string) => save !== id);
    localStorage.removeItem(id);
    cachedSaves[id] = undefined;
}

function openSave(id: string) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    saves.value[player.id]!.time = player.time;
    save();
    cachedSaves[player.id] = undefined;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    loadSave(saves.value[id]!);
    // Delete cached version in case of opening it again
    cachedSaves[id] = undefined;
}

function newFromPreset(preset: string) {
    // Reset preset dropdown
    selectedPreset.value = preset;
    nextTick(() => {
        selectedPreset.value = null;
    });

    if (preset[0] === "{") {
        // plaintext. No processing needed
    } else if (preset[0] === "e") {
        // Assumed to be base64, which starts with e
        preset = decodeURIComponent(escape(atob(preset)));
    } else if (preset[0] === "ᯡ") {
        // Assumed to be lz, which starts with ᯡ
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        preset = LZString.decompressFromUTF16(preset)!;
    } else {
        console.warn("Unable to determine preset encoding", preset);
        return;
    }
    const playerData = JSON.parse(preset);
    playerData.id = getUniqueID();
    save(playerData as PlayerData);

    settings.saves.push(playerData.id);

    openSave(playerData.id);
}

function editSave(id: string, newName: string) {
    const currSave = saves.value[id];
    if (currSave) {
        currSave.name = newName;
        if (player.id === id) {
            player.name = newName;
            save();
        } else {
            save(currSave as PlayerData);
            cachedSaves[id] = undefined;
        }
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    modelValue: isOpen.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((isOpen).value = $event)),
    ref_key: "modal",
    ref: modal
  }, {
    header: _withCtx(() => [
      _hoisted_1
    ]),
    body: _withCtx(({ shown }) => [
      shown
        ? (_openBlock(), _createBlock(_unref(Draggable), {
            key: 0,
            list: _unref(settings).saves,
            handle: ".handle",
            itemKey: (save) => save
          }, {
            item: _withCtx(({ element }) => [
              _createVNode(Save, {
                save: _unref(saves)[element],
                onOpen: ($event: any) => (openSave(element)),
                onExport: ($event: any) => (exportSave(element)),
                onEditName: name => editSave(element, name),
                onDuplicate: ($event: any) => (duplicateSave(element)),
                onDelete: ($event: any) => (deleteSave(element))
              }, null, 8, ["save", "onOpen", "onExport", "onEditName", "onDuplicate", "onDelete"])
            ]),
            _: 1
          }, 8, ["list", "itemKey"]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(Text, {
          modelValue: saveToImport.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((saveToImport).value = $event)),
          title: "Import Save",
          placeholder: "Paste your save here!",
          class: _normalizeClass({ importingFailed: importingFailed.value })
        }, null, 8, ["modelValue", "class"]),
        _createElementVNode("div", _hoisted_3, [
          _hoisted_4,
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("button", {
              class: "button",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (openSave(_unref(newSave)().id)))
            }, "New Game"),
            (Object.keys(_unref(bank)).length > 0)
              ? (_openBlock(), _createBlock(Select, {
                  key: 0,
                  options: _unref(bank),
                  modelValue: selectedPreset.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = preset => newFromPreset(preset as string)),
                  closeOnSelect: "",
                  placeholder: "Select preset",
                  class: "presets"
                }, null, 8, ["options", "modelValue"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _hoisted_7,
          _createElementVNode("button", {
            class: "button modal-default-button",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (isOpen.value = false))
          }, " Close ")
        ])
      ])
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})